"use client";

import { useEffect, useRef } from "react";

import { Input } from "@perceptron/ui/components/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@perceptron/ui/components/select";

import { env } from "@demo/env";
import { AVAILABLE_MODELS } from "@demo/shared/types";
import { useModelStore } from "@demo/store/model";

const MODEL_URL_PREFIX = "https://";
const MODEL_URL_SUFFIX = ".perceptron1.com";

const ModelSelector = () => {
  const selectedModel = useModelStore((state) => state.selectedModel);
  const setSelectedModel = useModelStore((state) => state.setSelectedModel);
  const customModelName = useModelStore((state) => state.customModelName);
  const setCustomModelName = useModelStore((state) => state.setCustomModelName);

  const inputRef = useRef<HTMLInputElement>(null);
  const wasCustomSelectedRef = useRef(!!customModelName);

  const isCustomSelected = !!customModelName;

  // Restore focus when transitioning from no custom value to having one
  useEffect(() => {
    if (isCustomSelected) {
      // Small delay to ensure the DOM has updated
      if (!wasCustomSelectedRef.current) {
        requestAnimationFrame(() => {
          inputRef.current?.focus();
        });
      }

      const customModel = AVAILABLE_MODELS.find((model) => model.name === "custom-model");

      if (!customModel) {
        return;
      }

      setSelectedModel(customModel);
    }
    wasCustomSelectedRef.current = isCustomSelected;
  }, [isCustomSelected, customModelName, setSelectedModel]);

  const handleValueChange = (value: string) => {
    setCustomModelName("");
    setSelectedModel(AVAILABLE_MODELS.find((model) => model.name === value)!);
  };

  const displayValue = isCustomSelected
    ? `${MODEL_URL_PREFIX}${customModelName}${MODEL_URL_SUFFIX}`
    : selectedModel.publicName;

  return (
    <Select value={isCustomSelected ? undefined : selectedModel.name} onValueChange={handleValueChange}>
      <SelectTrigger className="w-fit max-w-80 border-none bg-transparent hover:bg-background-tint focus:ring-0 focus:ring-offset-0 data-[state=open]:bg-background-tint">
        <span className="truncate label-2">{displayValue}</span>
      </SelectTrigger>
      <SelectContent>
        {AVAILABLE_MODELS.map((model) => {
          if (env.NEXT_PUBLIC_APP_ENV === "production" && !model.isPublic) {
            return null;
          }

          if (model.name === "custom-model") {
            return (
              <div
                className="relative flex w-full cursor-default select-none items-center gap-0 rounded-sm px-2 py-1.5 text-sm outline-none"
                key={model.name}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <span className="shrink-0 text-neutral-400">{MODEL_URL_PREFIX}</span>
                <Input
                  className="h-7 w-32 rounded-none border-x-0 border-y-0 border-b border-neutral-600 bg-transparent px-1 text-sm focus-visible:ring-0"
                  placeholder="model-name"
                  ref={inputRef}
                  value={customModelName}
                  onChange={(e) => setCustomModelName(e.target.value)}
                />
                <span className="shrink-0 text-neutral-400">{MODEL_URL_SUFFIX}</span>
              </div>
            );
          }
          return (
            <SelectItem className="cursor-pointer" key={model.name} value={model.name}>
              {model.publicName}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};

export default ModelSelector;
